import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "product-form" }
const _hoisted_2 = { class: "product-form__row" }
const _hoisted_3 = { class: "product-form__field" }
const _hoisted_4 = { class: "product-form__field" }
const _hoisted_5 = {
  key: 0,
  class: "product-form__lock-hint"
}
const _hoisted_6 = { class: "product-form__row" }
const _hoisted_7 = { class: "product-form__field" }
const _hoisted_8 = { class: "product-form__field" }

import { ref, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { Button, Input, Modal, Select } from "@/components/ui";
import { Icon } from "@iconify/vue";
import { ErrorMessage, Field, Form, defineRule } from "vee-validate";
import { useEditProduct } from "@/composables/product/useEditProduct";
import { useProductNames } from "@/composables/product/useProductNames";
import { useToast } from "@/composables/ui/useToast";
import { ProductPriceTypes, ProductPurchaseTypes } from "@/utils/enums";
import type { ProductNameDto } from "@/dtos/ProductName.dto";

interface Props {
  selectedProduct: Record<string, unknown>;
  opportunityId?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EditProductModal',
  props: {
    selectedProduct: {},
    opportunityId: {}
  },
  emits: ["closeEditProductModal", "productUpdated"],
  setup(__props: any, { emit: __emit }) {

defineRule("required", (value: string, [fieldName = "Field"]) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

// Separate from "required" so a valid $0 price (allowed for fixed-price products) isn't
// rejected by the falsy check above.
defineRule("requiredNumber", (value: string | number, [fieldName = "Field"]) => {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
    return `${fieldName} is required`;
  }
  return true;
});

const props = __props;

const emit = __emit;

const $toast = useToast();
const store = useStore();
const { mutate: updateProduct, isPending } = useEditProduct();

const editProductObj = ref<{
  id: string | null;
  name: string | null;
  price: number | string | null;
  quantity: number;
  tax: number | string | null;
  purchaseType: string | null;
}>({
  id: null,
  name: null,
  price: null,
  quantity: 1,
  tax: null,
  purchaseType: null,
});

// Product name catalog (organization-scoped, for the "select existing product" dropdown)
const productNamesParams = computed(() => {
  const organizationId = store.getters.account?.data?.organizationId;
  if (!organizationId) return null;
  return { organizationId };
});
const { data: productNamesData, isLoading: productNamesLoading } =
  useProductNames(productNamesParams);
const productNameCatalog = computed<ProductNameDto[]>(() => productNamesData.value || []);

const productNameOptions = computed(() => {
  const seen = new Set<string>();
  const options: { value: string; label: string }[] = [];
  // Always surface the product currently being edited, even if the catalog hasn't loaded it yet
  if (editProductObj.value.name) {
    seen.add(editProductObj.value.name);
    options.push({ value: editProductObj.value.name, label: editProductObj.value.name });
  }
  for (const product of productNameCatalog.value) {
    if (!product?.name || !product.isActive || seen.has(product.name)) continue;
    seen.add(product.name);
    options.push({ value: product.name, label: product.name });
  }
  return options;
});

const findCatalogProductByName = (name: string) => {
  return productNameCatalog.value.find((product) => product.name === name);
};

// A "fixed" priced product (set in Settings > Products) locks the Price field —
// reps can't override it on the deal. "Flexible" (or a brand-new name) stays editable.
const selectedCatalogProduct = computed(() =>
  findCatalogProductByName(editProductObj.value.name || "")
);
const isPriceLocked = computed(
  () => selectedCatalogProduct.value?.priceType === ProductPriceTypes.fixed
);

const isCreatingNewProductName = ref(false);
const newProductNameInput = ref<{ focus?: () => void } | null>(null);

const onProductNameSelected = (name: string, field: { onChange: (v: string) => void }) => {
  field.onChange(name);
  const match = findCatalogProductByName(name);
  if (match) {
    editProductObj.value.price = match.price ?? null;
  }
};

const startCreatingNewProduct = () => {
  isCreatingNewProductName.value = true;
  editProductObj.value.name = null;
  // Don't carry over a price (possibly locked) that belonged to the previously selected catalog product.
  editProductObj.value.price = null;
  nextTick(() => newProductNameInput.value?.focus?.());
};

const cancelCreatingNewProduct = () => {
  isCreatingNewProductName.value = false;
  editProductObj.value.name = null;
};

const showModal = computed(
  () =>
    props.selectedProduct &&
    typeof props.selectedProduct === "object" &&
    Object.keys(props.selectedProduct).length > 0
);

const purchaseTypeOptions = computed(() =>
  Object.values(ProductPurchaseTypes).map((type) => ({
    value: type,
    label: type,
  }))
);

function setValues(product: Record<string, unknown> | undefined) {
  if (!product || Object.keys(product).length === 0) return;
  editProductObj.value.id = (product.id as string) || null;
  editProductObj.value.name = (product.name as string) || null;
  editProductObj.value.price = (product.price as number) ?? null;
  editProductObj.value.quantity = (product.quantity as number) ?? 1;
  editProductObj.value.tax = (product.tax as number) ?? null;
  editProductObj.value.purchaseType = (product.purchaseType as string) || null;
}

function closeModal() {
  emit("closeEditProductModal");
}

function editProduct() {
  const id = editProductObj.value.id;
  const name = editProductObj.value.name;
  const purchaseType = editProductObj.value.purchaseType;
  if (!id || !name || !purchaseType) {
    $toast?.error("Product ID is required to edit. Please save the opportunity first.");
    return;
  }

  const payload = {
    id,
    name,
    price: parseFloat(String(editProductObj.value.price)) || 0,
    quantity: parseInt(String(editProductObj.value.quantity), 10) || 1,
    tax:
      editProductObj.value.tax != null ? parseFloat(String(editProductObj.value.tax)) : undefined,
    purchaseType,
    opportunityId: props.opportunityId,
  };

  updateProduct(payload, {
    onSuccess: (data) => {
      $toast?.success("Product updated successfully");
      emit("productUpdated", data);
      closeModal();
    },
    onError: (error: Error) => {
      $toast?.error(error.message || "Failed to update product");
    },
  });
}

watch(
  () => props.selectedProduct,
  (v) => setValues(v),
  { immediate: true, deep: true }
);

return (_ctx: any,_cache: any) => {
  return (showModal.value)
    ? (_openBlock(), _createBlock(_unref(Modal), {
        key: 0,
        show: true,
        headerVariant: "blue",
        closeOnBackdrop: false,
        blurBackdrop: false,
        backdropOpacity: 0.5,
        "show-close-button": false,
        size: "md",
        onClose: closeModal
      }, {
        header: _withCtx(() => [
          _cache[5] || (_cache[5] = _createElementVNode("div", { class: "product-header__text" }, [
            _createElementVNode("h2", { class: "product-header__title" }, "Edit Product"),
            _createElementVNode("p", { class: "product-header__subtitle" }, "Update product details")
          ], -1)),
          _createElementVNode("button", {
            type: "button",
            class: "product-header__close",
            onClick: closeModal,
            "aria-label": "Close"
          }, [
            _createVNode(_unref(Icon), {
              icon: "mdi:close",
              width: "18",
              height: "18"
            })
          ])
        ]),
        body: _withCtx(() => [
          _createVNode(_unref(Form), {
            id: "edit-product-form",
            onSubmit: editProduct
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(_unref(Field), {
                      name: "name",
                      rules: { required: ['Name'] },
                      "model-value": editProductObj.value.name,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (editProductObj.value.name = $event))
                    }, {
                      default: _withCtx(({ field }) => [
                        (isCreatingNewProductName.value)
                          ? (_openBlock(), _createBlock(_unref(Input), {
                              key: 0,
                              ref_key: "newProductNameInput",
                              ref: newProductNameInput,
                              "model-value": field.value,
                              "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                              onBlur: field.onBlur,
                              label: "Name",
                              type: "text",
                              required: true,
                              placeholder: "Enter new product name"
                            }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur"]))
                          : (_openBlock(), _createBlock(_unref(Select), {
                              key: 1,
                              "model-value": field.value,
                              "onUpdate:modelValue": ($event: any) => (onProductNameSelected($event, field)),
                              onBlur: field.onBlur,
                              options: productNameOptions.value,
                              loading: _unref(productNamesLoading),
                              label: "Name",
                              required: true,
                              placeholder: "Select or search product",
                              searchable: "",
                              "search-placeholder": "Search products...",
                              "show-add-button": "",
                              "add-button-text": "New product name",
                              onAddNew: startCreatingNewProduct
                            }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur", "options", "loading"]))
                      ]),
                      _: 1
                    }, 8, ["model-value"]),
                    (isCreatingNewProductName.value)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          type: "button",
                          class: "product-form__back-link",
                          onClick: cancelCreatingNewProduct
                        }, [
                          _createVNode(_unref(Icon), {
                            icon: "tabler:corner-up-left",
                            width: "12",
                            height: "12"
                          }),
                          _cache[6] || (_cache[6] = _createTextVNode(" Choose from existing products ", -1))
                        ]))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(ErrorMessage), {
                      name: "name",
                      class: "error-message"
                    })
                  ]),
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_unref(Field), {
                      name: "price",
                      rules: { requiredNumber: ['Price'] },
                      "model-value": editProductObj.value.price,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (editProductObj.value.price = $event))
                    }, {
                      default: _withCtx(({ field }) => [
                        _createVNode(_unref(Input), {
                          "model-value": field.value,
                          "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                          onBlur: field.onBlur,
                          label: "Price",
                          type: "number",
                          required: true,
                          disabled: isPriceLocked.value,
                          placeholder: "0.00"
                        }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur", "disabled"])
                      ]),
                      _: 1
                    }, 8, ["model-value"]),
                    (isPriceLocked.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                          _createVNode(_unref(Icon), {
                            icon: "tabler:lock",
                            width: "11",
                            height: "11"
                          }),
                          _cache[7] || (_cache[7] = _createTextVNode(" Fixed price — set in Settings ", -1))
                        ]))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(ErrorMessage), {
                      name: "price",
                      class: "error-message"
                    })
                  ])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_unref(Field), {
                      name: "quantity",
                      "model-value": editProductObj.value.quantity,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (editProductObj.value.quantity = $event))
                    }, {
                      default: _withCtx(({ field }) => [
                        _createVNode(_unref(Input), {
                          "model-value": field.value,
                          "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                          onBlur: field.onBlur,
                          label: "Quantity",
                          type: "number",
                          placeholder: "1"
                        }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur"])
                      ]),
                      _: 1
                    }, 8, ["model-value"])
                  ]),
                  _createElementVNode("div", _hoisted_8, [
                    _createVNode(_unref(Field), {
                      name: "tax",
                      "model-value": editProductObj.value.tax,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (editProductObj.value.tax = $event))
                    }, {
                      default: _withCtx(({ field }) => [
                        _createVNode(_unref(Input), {
                          "model-value": field.value,
                          "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                          onBlur: field.onBlur,
                          label: "Tax %",
                          type: "number",
                          placeholder: "0.00"
                        }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur"])
                      ]),
                      _: 1
                    }, 8, ["model-value"])
                  ])
                ]),
                _createElementVNode("div", null, [
                  _createVNode(_unref(Field), {
                    name: "purchaseType",
                    rules: { required: ['Subscription Type'] },
                    "model-value": editProductObj.value.purchaseType,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (editProductObj.value.purchaseType = $event))
                  }, {
                    default: _withCtx(({ field }) => [
                      _createVNode(_unref(Select), {
                        "model-value": field.value,
                        "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                        onBlur: field.onBlur,
                        options: purchaseTypeOptions.value,
                        label: "Subscription Type",
                        required: true,
                        placeholder: "Select subscription type"
                      }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur", "options"])
                    ]),
                    _: 1
                  }, 8, ["model-value"]),
                  _createVNode(_unref(ErrorMessage), {
                    name: "purchaseType",
                    class: "error-message"
                  })
                ])
              ])
            ]),
            _: 1
          })
        ]),
        footer: _withCtx(() => [
          _createVNode(_unref(Button), {
            variant: "secondary",
            size: "sm",
            onClick: closeModal
          }, {
            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
              _createTextVNode("Cancel", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_unref(Button), {
            variant: "primary",
            size: "sm",
            type: "submit",
            form: "edit-product-form",
            loading: _unref(isPending)
          }, {
            default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
              _createTextVNode(" Save Changes ", -1)
            ]))]),
            _: 1
          }, 8, ["loading"])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})